import type { FitnessValues, WeekDay } from '@painpal/contracts';
import { SPORTS } from '@painpal/core';
import { Layers } from 'lucide-react';
import { Fragment } from 'react';
import type { SportFilter } from '../../api/keys';
import { formatWeekdayDay, weekdayLetter } from '../../lib/format';
import { SPORT_META } from '../../lib/sports';
import { SPORT_COLORS } from '../../theme/tokens';
import type { SwipeHandlers } from '../../ui/useSwipe';
import {
  barLabel, barPx, dayLine, daySegments, daySports, formParts, metricValue, scaleFor, totalsText, trackPx, weekTotals, type WeekMetric,
} from './weekSnapshotModel';
import styles from './weekSnapshot.module.css';

interface StackProps { day: WeekDay; metric: WeekMetric; scale: number; track: number }

// The bar keeps its scaleY animation; inside it each sport takes its share of the height, bottom up.
function DayStack({ day, metric, scale, track }: StackProps) {
  const value = metricValue(day, metric);
  if (value === 0) return <span className={styles.stub} />;
  return (
    <span className={styles.bar} data-part="bar" style={{ transform: `scaleY(${barPx(value, scale, track) / track})` }}>
      {daySegments(day, metric).map((segment) => (
        <span key={segment.sport} className={styles.segment} data-sport={segment.sport} style={{ flexGrow: segment.value, background: SPORT_COLORS[segment.sport] }} />
      ))}
    </span>
  );
}

// Phone columns are ~42px wide, too narrow for the icons beside the value and the N× marker; there the bar colours name the sports.
function DayValue({ day, metric, isPhone }: { day: WeekDay; metric: WeekMetric; isPhone: boolean }) {
  const value = metricValue(day, metric);
  return (
    <span className={styles.valueRow}>
      {!isPhone && (
        <span className={styles.icons} aria-hidden="true">
          {daySports(day).map((sport) => {
            const Icon = SPORT_META[sport].Icon;
            return <Icon key={sport} size={10} />;
          })}
        </span>
      )}
      {metric === 'load' && day.noLoad > 0 && <span className={styles.noPower} data-no-power aria-hidden="true" />}
      <span className={styles.dayValue} data-zero={value === 0}>{barLabel(value, metric)}</span>
      {day.activities > 1 && <span className={styles.multi}>{day.activities}×</span>}
    </span>
  );
}

interface DayProps {
  day: WeekDay; metric: WeekMetric; sport: SportFilter; scale: number; track: number; today: string; selected: boolean; isPhone: boolean;
  onSelect: (date: string) => void;
}

function DayColumn({ day, metric, sport, scale, track, today, selected, isPhone, onSelect }: DayProps) {
  const future = day.date > today;
  const isToday = day.date === today;
  return (
    <button
      type="button" className={styles.day} aria-pressed={selected} aria-label={dayLine(day, metric, sport)}
      aria-current={isToday ? 'date' : undefined} data-today={isToday}
      aria-disabled={future || undefined} onClick={future ? undefined : () => onSelect(day.date)}
    >
      <span className={styles.well} style={{ height: track }}>
        {!future && <DayStack day={day} metric={metric} scale={scale} track={track} />}
      </span>
      <span className={styles.label}>
        <span className={styles.dayName}>{isPhone ? weekdayLetter(day.date) : formatWeekdayDay(day.date)}</span>
        {!future && <DayValue day={day} metric={metric} isPhone={isPhone} />}
      </span>
    </button>
  );
}

interface StripProps {
  days: WeekDay[]; metric: WeekMetric; sport: SportFilter; today: string; selected: string | null; isPhone: boolean; placeholder: boolean;
  onSelect: (date: string) => void; swipe?: SwipeHandlers;
}

export function WeekDayStrip({ days, metric, sport, today, selected, isPhone, placeholder, onSelect, swipe }: StripProps) {
  const scale = scaleFor(days, metric);
  const track = trackPx(isPhone);
  return (
    <div className={styles.strip} role="group" aria-label="Days" data-placeholder={placeholder} {...swipe}>
      {days.map((day) => (
        <DayColumn
          key={day.date} day={day} metric={metric} sport={sport} scale={scale} track={track} today={today}
          selected={day.date === selected} isPhone={isPhone} onSelect={onSelect}
        />
      ))}
    </div>
  );
}

interface TotalsProps { days: WeekDay[]; metric: WeekMetric; current: boolean; selected: string | null; sport: SportFilter; compact: boolean }

export function WeekTotals({ days, metric, current, selected, sport, compact }: TotalsProps) {
  const picked = days.find((d) => d.date === selected);
  if (picked !== undefined) return <p className={styles.totals}><span className={styles.dayLine}>{dayLine(picked, metric, sport)}</span></p>;
  const { value, rest } = totalsText(weekTotals(days), metric, current, sport, compact);
  return <p className={styles.totals}><span className={styles.totalValue}>{value}</span> {rest}</p>;
}

interface FormLineProps { end: FitnessValues | null; previous: FitnessValues | null; current: boolean; isPhone: boolean; filtered: boolean }

// Real spaces between the parts: in the mono face they give the board's 6px (desktop) and ~5px (phone) gaps.
// Filtered, the line says it still covers every sport; desktop adds the NOT FILTERED chip.
export function WeekFormLine({ end, previous, current, isPhone, filtered }: FormLineProps) {
  if (end === null) return null;
  return (
    <p className={styles.form}>
      {formParts(end, previous).map((part, i) => (
        <Fragment key={part.label}>
          {i > 0 && ' · '}
          <span className={styles.formLabel}>{part.label}</span>{' '}
          <span className={styles.formValue} data-positive={part.positive}>{part.value}</span>
          {part.delta !== null && ` ${part.delta}`}
        </Fragment>
      ))}
      {current && !isPhone && <> · <span className={styles.formLabel}>SO FAR</span></>}
      {filtered && <> · <span className={styles.formLabel}>ALL SPORTS</span></>}
      {filtered && !isPhone && <span className={styles.formChip}><Layers size={10} aria-hidden="true" />NOT FILTERED</span>}
    </p>
  );
}

const STACK_ORDER = 'STACK ORDER, BOTTOM UP: RIDE · RUN · SWIM · WALK/HIKE · STRENGTH · OTHER';

// The week's sports in SPORTS order, which is also the stack order.
export function SportKey({ days }: { days: WeekDay[] }) {
  const sports = SPORTS.filter((sport) => days.some((d) => d.bySport[sport] !== undefined));
  if (sports.length === 0) return null;
  return (
    <ul className={styles.sportKey} aria-label="Sport key">
      {sports.map((sport) => {
        const { Icon, label } = SPORT_META[sport];
        return <li key={sport}><span className={styles.keySwatch} style={{ background: SPORT_COLORS[sport] }} aria-hidden="true" /><Icon size={11} aria-hidden="true" />{label}</li>;
      })}
      <li className={styles.stackOrder}>{STACK_ORDER}</li>
    </ul>
  );
}
